'use client'
import { BannerDecision } from '@/app/api/placements/types'
import { DeviceType } from '@/app/util/env'
import UserContext from '@/context/user-context'
import { getAdBannerData } from '@/util/ad-banner'
import { fireAdTrackingPixel } from '@/util/ad-click'
import { cn } from '@/util/cn'
import { pingback, PingbackActionType } from '@giphy/js-analytics'
import { useContext } from 'react'
import { InView } from 'react-intersection-observer'

const CenturionBanner = ({
    className,
    aspectRatio,
    bannerData,
    deviceType,
}: {
    className?: string
    aspectRatio?: number
    bannerData: BannerDecision
    deviceType: DeviceType
}) => {
    const { user } = useContext(UserContext)
    const gif = bannerData?.gif
    if (!gif) {
        return null
    }

    const { url, fileType } = getAdBannerData(gif, deviceType)
    if (!url) {
        return null
    }

    const {
        click_url,
        click_tracking_url,
        cta_text,
        impression_tracking_url: impression_url,
        third_party_tag,
    } = bannerData
    const { width, height } = gif.images.original

    const trackAction = (actionType: PingbackActionType) => {
        pingback({
            userId: user ? user.id : undefined,
            actionType,
            analyticsResponsePayload: gif.analytics_response_payload,
        })
    }

    const onClickHandler = () => {
        trackAction('CLICK')
        fireAdTrackingPixel(click_tracking_url)
    }

    const onMouseOverHandler = () => {
        trackAction('HOVER')
    }

    const onView = (inView: boolean): void => {
        if (inView) {
            try {
                fireAdTrackingPixel(impression_url)
                fireAdTrackingPixel(third_party_tag)
            } catch (e) {
                console.error(`Error with tracking pixel for geotargeted banner`, e)
            }
        }
    }

    return (
        <InView
            key={url}
            as="a"
            onChange={onView}
            triggerOnce
            threshold={1}
            href={click_url}
            onClick={onClickHandler}
            onMouseOver={onMouseOverHandler}
            target="_blank"
            data-testid="centurion-banner-link"
            className={cn('ci-removed block transition-all duration-100', className, click_url && 'cursor-pointer')}
            style={{ aspectRatio: aspectRatio || width / height }}
        >
            {fileType === 'video' ? (
                <video
                    src={url}
                    title="Home banner"
                    autoPlay
                    loop
                    muted
                    className="ci-hidden w-full"
                    data-testid="centurion-banner-media"
                    style={{ aspectRatio }}
                />
            ) : (
                <img
                    className="h-full w-full object-cover"
                    alt={cta_text}
                    src={url}
                    title="Home banner"
                    data-testid="centurion-banner-media"
                />
            )}
        </InView>
    )
}

export default CenturionBanner
